"use client";

import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  CheckCircle2,
  AlertCircle,
  Loader2,
  User,
  Mail,
  FileText,
  MessageSquare,
} from "lucide-react";
import HoverArrow from "@/components/shared/HoverArrow";

type ContactState = {
  status: "idle" | "success" | "error";
  message?: string;
};

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (
        siteKeyOrWidgetId: string | number,
        options?: { action: string },
      ) => Promise<string>;
      render: (
        container: HTMLElement | string,
        parameters: Record<string, any>,
      ) => number;
      reset: (widgetId?: number) => void;
    };
  }
}

type RecaptchaTokens = {
  recaptchaV3Token?: string;
  recaptchaV2Token?: string;
  recaptchaAction: "contact";
};


async function loadRecaptchaScript(renderKey: string) {
  if (typeof window === "undefined") return;
  if (window.grecaptcha) return;

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${renderKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load reCAPTCHA script."));
    document.body.appendChild(script);
  });
}

async function getRecaptchaV3Token(): Promise<string> {
  const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY;
  if (!RECAPTCHA_SITE_KEY)
    throw new Error("reCAPTCHA v3 site key is not configured.");
  if (typeof window === "undefined")
    throw new Error("reCAPTCHA v3 requires a browser environment.");

  await loadRecaptchaScript(RECAPTCHA_SITE_KEY);

  return new Promise<string>((resolve, reject) => {
    const grecaptcha = window.grecaptcha;
    if (!grecaptcha?.ready) {
      reject(new Error("reCAPTCHA v3 is not available."));
      return;
    }

    grecaptcha.ready(() => {
      if (!grecaptcha.execute) {
        reject(new Error("reCAPTCHA v3 execute is not available."));
        return;
      }

      grecaptcha
        .execute(RECAPTCHA_SITE_KEY, { action: "contact" })
        .then(resolve)
        .catch(reject);
    });
  });
}

async function getRecaptchaV2Token(
  containerRoot?: HTMLElement | null,
): Promise<string> {
  const RECAPTCHA_V2_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY;
  const siteKey = RECAPTCHA_V2_SITE_KEY;
  if (!siteKey) throw new Error("reCAPTCHA v2 site key is not configured.");
  if (typeof window === "undefined")
    throw new Error("reCAPTCHA v2 requires a browser environment.");

  await loadRecaptchaScript("explicit");

  return new Promise<string>((resolve, reject) => {
    const grecaptcha = window.grecaptcha;
    if (!grecaptcha?.ready || !grecaptcha?.render) {
      reject(new Error("reCAPTCHA v2 is not available."));
      return;
    }

    grecaptcha.ready(() => {
      const root = containerRoot ?? document.body;
      const container = document.createElement("div");
      container.style.marginTop = "1rem";
      root.appendChild(container);

      let widgetId: number | undefined;
      const cleanup = () => {
        if (typeof widgetId === "number") {
          try {
            grecaptcha.reset(widgetId);
          } catch {
            /* ignore */
          }
        }
        container.remove();
      };

      try {
        widgetId = grecaptcha.render(container, {
          sitekey: siteKey,
          size: "normal",
          callback: (token: string) => {
            resolve(token);
            cleanup();
          },
          "error-callback": () => {
            cleanup();
            reject(new Error("reCAPTCHA v2 challenge failed."));
          },
          "expired-callback": () => {
            cleanup();
            reject(new Error("reCAPTCHA v2 token expired."));
          },
        });
      } catch (error) {
        cleanup();
        reject(error);
        return;
      }

      try {
        // For visible v2, the widget itself will prompt the user.
        // No manual execute call is needed if the widget renders normally.
      } catch (error) {
        cleanup();
        reject(error);
      }
    });
  });
}

async function getRecaptchaTokens(
  containerRoot?: HTMLElement | null,
): Promise<RecaptchaTokens> {
  const RECAPTCHA_V2_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_V2_SITE_KEY;
  if (typeof window === "undefined") {
    return { recaptchaAction: "contact" };
  }
  const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_V3_SITE_KEY;
  if (RECAPTCHA_SITE_KEY) {
    try {
      const recaptchaV3Token = await getRecaptchaV3Token();
      return { recaptchaV3Token, recaptchaAction: "contact" };
    } catch (error) {
      console.warn("reCAPTCHA v3 failed, falling back to v2:", error);
    }
  }

  if (RECAPTCHA_V2_SITE_KEY) {
    const recaptchaV2Token = await getRecaptchaV2Token(containerRoot);
    return { recaptchaV2Token, recaptchaAction: "contact" };
  }

  return { recaptchaAction: "contact" };
}

function Field({
  label,
  name,
  type = "text",
  placeholder,
  required = true,
  textarea = false,
  disabled = false,
}: {
  label: string;
  name: string;
  type?: string;
  placeholder: string;
  required?: boolean;
  textarea?: boolean;
  disabled?: boolean;
}) {
  const base =
    "w-full bg-paper border border-line rounded-xl text-ink placeholder:text-ink-faint text-base focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <div className="flex flex-col gap-2">
      <label className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-ink-faint">
        {label}
        {required && <span className="text-accent">*</span>}
      </label>
      {textarea ? (
        <textarea
          name={name}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          rows={5}
          className={`${base} px-4 py-3 resize-none`}
        />
      ) : (
        <input
          type={type}
          name={name}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className={`${base} px-4 py-3`}
        />
      )}
    </div>
  );
}

export default function KontaktForm() {
  const [state, setState] = useState<ContactState>({ status: "idle" });
  const [pending, setPending] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const recaptchaContainerRef = useRef<HTMLDivElement>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const CONTACT_ENDPOINT = process.env.NEXT_PUBLIC_CONTACT_ENDPOINT;

    const formData = new FormData(e.currentTarget);
    const name = (formData.get("name") as string | null)?.trim() ?? "";
    const email = (formData.get("email") as string | null)?.trim() ?? "";
    const subject = (formData.get("subject") as string | null)?.trim() ?? "";
    const message = (formData.get("message") as string | null)?.trim() ?? "";

    if (!name || !email || !subject || !message) {
      setState({ status: "error", message: "Bitte alle Felder ausfüllen." });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setState({
        status: "error",
        message: "Bitte eine gültige E-Mail-Adresse eingeben.",
      });
      return;
    }

    if (!CONTACT_ENDPOINT) {
      setState({
        status: "error",
        message:
          "Kontaktformular ist nicht konfiguriert. Bitte schreib uns direkt unter info@ncrechner.de.",
      });
      return;
    }

    setPending(true);
    try {
      const tokens = await getRecaptchaTokens(recaptchaContainerRef.current);

      const payload: Record<string, string | undefined> = {
        name,
        email,
        subject,
        message,
        ...tokens,
      };

      const res = await fetch(`${CONTACT_ENDPOINT}/contact`, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = await res.text().catch(() => "");
        throw new Error(body || `HTTP ${res.status}`);
      }

      setState({ status: "success" });
      formRef.current?.reset();
    } catch (err) {
      console.error("[contact] send failed:", err);
      setState({
        status: "error",
        message:
          "Beim Senden ist ein Fehler aufgetreten. Bitte versuche es erneut oder kontaktiere uns direkt unter info@ncrechner.de",
      });
    } finally {
      setPending(false);
    }
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="flex flex-col gap-5">
      {/* Status banners */}
      <AnimatePresence mode="wait">
        {state.status === "success" && (
          <motion.div
            key="success"
            initial={{ opacity: 0, y: -10, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200"
          >
            <CheckCircle2
              size={20}
              className="text-emerald-600 shrink-0 mt-0.5"
            />
            <div>
              <div className="text-emerald-800 font-bold text-sm">
                Erfolgreich gesendet!
              </div>
              <div className="text-emerald-700 text-sm mt-0.5 leading-relaxed">
                Deine Anfrage wurde erfolgreich an ncrechner gesendet. Wir
                melden uns bald bei dir. Bitte check auch deinen Posteingang —
                du hast eine Bestätigung erhalten.
              </div>
            </div>
          </motion.div>
        )}
        {state.status === "error" && (
          <motion.div
            key="error"
            initial={{ opacity: 0, y: -10, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-start gap-3 p-4 rounded-xl bg-rose-50 border border-rose-200"
          >
            <AlertCircle size={20} className="text-rose-500 shrink-0 mt-0.5" />
            <div>
              <div className="text-rose-700 font-bold text-sm">Fehler</div>
              <div className="text-rose-600 text-sm mt-0.5">
                {state.message}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fields */}
      <div className="grid sm:grid-cols-2 gap-5">
        <Field
          label="Name"
          name="name"
          placeholder="Dein vollständiger Name"
          disabled={pending}
        />
        <Field
          label="E-Mail-Adresse"
          name="email"
          type="email"
          placeholder="deine@email.de"
          disabled={pending}
        />
      </div>

      <Field
        label="Betreff"
        name="subject"
        placeholder="Womit können wir dir helfen?"
        disabled={pending}
      />

      <Field
        label="Nachricht"
        name="message"
        placeholder="Schreib uns deine Frage, dein Feedback oder deinen Verbesserungsvorschlag..."
        textarea
        disabled={pending}
      />

      <div ref={recaptchaContainerRef} />

      {/* Submit */}
      <button
        type="submit"
        disabled={pending}
        className="group mt-1 flex items-center justify-center gap-2.5 w-full px-6 py-4 rounded-full bg-accent text-paper font-semibold text-base min-h-[52px] cursor-pointer hover:bg-accent-deep transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {pending ? (
          <>
            <Loader2 size={17} className="animate-spin" />
            Wird gesendet…
          </>
        ) : (
          <>
            <Send size={16} />
            Nachricht senden
            <HoverArrow size={16} />
          </>
        )}
      </button>

      <p className="text-center text-ink-faint text-xs">
        Mit dem Absenden stimmst du unserer{" "}
        <a
          href="/datenschutz/"
          className="underline decoration-line underline-offset-2 hover:text-accent transition-colors"
        >
          Datenschutzerklärung
        </a>{" "}
        zu.
      </p>
    </form>
  );
}
